import * as actionTypes from "../constants/userConstants";
import * as cartActionTypes from "../constants/cartConstants";
import api from "../utils/axiosInstance";
import { getCart, mergeGuestCart } from "./cartActions";
import {
  USER_FACING_FALLBACK,
  getApiErrorMessage,
} from "../utils/errorMessages";
import { normalizeRegistrationError } from "../utils/registrationErrors";

const VALID_ROLES = [
  "user",
  "customer",
  "admin",
  "super_admin",
  "store_manager",
  "editor",
];

function normalizeRoles(value) {
  const rawRoles = Array.isArray(value)
    ? value
    : String(value || "")
        .split(",")
        .map((role) => role.trim());

  const roles = [
    ...new Set(
      rawRoles
        .map((role) => String(role).trim().toLowerCase())
        .filter((role) => VALID_ROLES.includes(role)),
    ),
  ];

  return roles.length ? roles : ["user"];
}

function normalizeUser(user) {
  if (!user) return null;

  const defaultShippingAddress =
    user.defaultShippingAddress || user.checkoutDefaults?.shippingAddress || {};

  return {
    ...user,
    title: user.title || "",
    name: user.name || "",
    lastName: user.lastName || "",
    roles: normalizeRoles(user.roles ?? user.role),
    defaultShippingAddress,
    checkoutDefaults: {
      ...(user.checkoutDefaults || {}),
      shippingAddress: defaultShippingAddress,
    },
  };
}

const saveUserToStorage = (user) => {
  if (typeof window === "undefined") return normalizeUser(user);

  const normalizedUser = normalizeUser(user);

  if (normalizedUser) {
    localStorage.setItem("userInfo", JSON.stringify(normalizedUser));
  } else {
    localStorage.removeItem("userInfo");
  }

  return normalizedUser;
};

const getErrorMessage = (error, fallback = USER_FACING_FALLBACK) =>
  getApiErrorMessage(error, { fallback });

/* -------------------------------------------------------------------------- */
/*                                Reset actions                               */
/* -------------------------------------------------------------------------- */

export const resetUserLogin = () => ({
  type: actionTypes.USER_LOGIN_RESET,
});

export const resetUserRegister = () => ({
  type: actionTypes.USER_REGISTER_RESET,
});

export const resetUserEmailVerify = () => ({
  type: actionTypes.USER_EMAIL_VERIFY_RESET,
});

export const resetUserEmailResend = () => ({
  type: actionTypes.USER_EMAIL_RESEND_RESET,
});

export const resetUserProfileUpdate = () => ({
  type: actionTypes.USER_PROFILE_UPDATE_RESET,
});

export const resetUserEmailChangeRequest = () => ({
  type: actionTypes.USER_EMAIL_CHANGE_REQUEST_RESET,
});

export const resetUserEmailChangeVerify = () => ({
  type: actionTypes.USER_EMAIL_CHANGE_VERIFY_RESET,
});

export const resetForgotPassword = () => ({
  type: actionTypes.USER_FORGOT_PASSWORD_RESET,
});

export const resetResetPassword = () => ({
  type: actionTypes.USER_RESET_PASSWORD_RESET,
});

export const resetAdminUserList = () => ({
  type: actionTypes.ADMIN_USER_LIST_RESET,
});

export const resetAdminUserDetails = () => ({
  type: actionTypes.ADMIN_USER_DETAILS_RESET,
});

export const resetAdminUserCreate = () => ({
  type: actionTypes.ADMIN_USER_CREATE_RESET,
});

export const resetAdminUserUpdate = () => ({
  type: actionTypes.ADMIN_USER_UPDATE_RESET,
});

export const resetAdminUserDelete = () => ({
  type: actionTypes.ADMIN_USER_DELETE_RESET,
});

/* -------------------------------------------------------------------------- */
/*                               Auth / Session                               */
/* -------------------------------------------------------------------------- */

export const checkUserSession = () => async (dispatch) => {
  try {
    dispatch({ type: actionTypes.USER_AUTH_REQUEST });

    const { data } = await api.get("/auth/check-session");

    if (data?.authenticated && data?.user) {
      const user = saveUserToStorage(data.user);

      dispatch({
        type: actionTypes.USER_AUTH_SUCCESS,
        payload: user,
      });

      return user;
    }

    saveUserToStorage(null);
    dispatch({ type: actionTypes.USER_AUTH_RESET });
    return null;
  } catch (error) {
    saveUserToStorage(null);

    if (error?.response?.status === 401) {
      dispatch({ type: actionTypes.USER_AUTH_RESET });
    } else {
      dispatch({
        type: actionTypes.USER_AUTH_FAIL,
        payload: getErrorMessage(error),
      });
    }

    return null;
  }
};

export const login = (email, password) => async (dispatch) => {
  try {
    dispatch({ type: actionTypes.USER_LOGIN_REQUEST });

    const { data } = await api.post("/auth/login", { email, password });

    const user = saveUserToStorage(data.user);

    dispatch({ type: actionTypes.USER_LOGIN_SUCCESS });

    dispatch({
      type: actionTypes.USER_AUTH_SUCCESS,
      payload: user,
    });

    await dispatch(mergeGuestCart());
    await dispatch(getCart());

    return {
      ...data,
      user,
    };
  } catch (error) {
    const payload = {
      message: getErrorMessage(error),
      code: String(error?.response?.data?.code || error?.errorCode || ""),
      verificationRequired: Boolean(error?.response?.data?.verificationRequired),
      email,
    };

    if (payload.code === "EMAIL_NOT_VERIFIED") {
      saveUserToStorage(null);
      dispatch({ type: actionTypes.USER_AUTH_RESET });
    }

    dispatch({
      type: actionTypes.USER_LOGIN_FAIL,
      payload,
    });

    return payload;
  }
};

export const register = (payload) => async (dispatch) => {
  try {
    dispatch({ type: actionTypes.USER_REGISTER_REQUEST });

    const { data } = await api.post("/auth/register", payload);

    if (data?.verificationRequired && !data?.user) {
      saveUserToStorage(null);
      dispatch({ type: actionTypes.USER_AUTH_RESET });
      dispatch({
        type: actionTypes.USER_REGISTER_SUCCESS,
        payload: {
          ...data,
          email: payload?.email || "",
        },
      });

      return {
        ...data,
        email: payload?.email || "",
      };
    }

    const user = saveUserToStorage(data.user);

    dispatch({
      type: actionTypes.USER_REGISTER_SUCCESS,
      payload: {
        ...data,
        user,
      },
    });

    dispatch({
      type: actionTypes.USER_AUTH_SUCCESS,
      payload: user,
    });

    await dispatch(mergeGuestCart());
    await dispatch(getCart());

    return {
      ...data,
      user,
    };
  } catch (error) {
    const errorPayload = normalizeRegistrationError(error);

    if (errorPayload.verificationRequired) {
      saveUserToStorage(null);
      dispatch({ type: actionTypes.USER_AUTH_RESET });
    }

    dispatch({
      type: actionTypes.USER_REGISTER_FAIL,
      payload: errorPayload,
    });

    return errorPayload;
  }
};

export const verifyEmail = (email, code) => async (dispatch) => {
  try {
    dispatch({ type: actionTypes.USER_EMAIL_VERIFY_REQUEST });

    const { data } = await api.post("/auth/verify-email", { email, code });
    const user = data?.user ? saveUserToStorage(data.user) : null;

    dispatch({
      type: actionTypes.USER_EMAIL_VERIFY_SUCCESS,
      payload: {
        ...data,
        user,
      },
    });

    if (user) {
      dispatch({
        type: actionTypes.USER_AUTH_SUCCESS,
        payload: user,
      });

      await dispatch(mergeGuestCart());
      await dispatch(getCart());
    }

    return {
      ...data,
      user,
    };
  } catch (error) {
    const payload = {
      message: getErrorMessage(error),
      code: String(error?.response?.data?.code || error?.errorCode || ""),
      verificationRequired: Boolean(error?.response?.data?.verificationRequired),
      email,
    };

    dispatch({
      type: actionTypes.USER_EMAIL_VERIFY_FAIL,
      payload,
    });

    return payload;
  }
};

export const resendVerificationCode = (email) => async (dispatch) => {
  try {
    dispatch({ type: actionTypes.USER_EMAIL_RESEND_REQUEST });

    const { data } = await api.post("/auth/resend-verification", { email });

    dispatch({
      type: actionTypes.USER_EMAIL_RESEND_SUCCESS,
      payload: data.message,
    });

    return data;
  } catch (error) {
    const payload = normalizeRegistrationError(error);

    dispatch({
      type: actionTypes.USER_EMAIL_RESEND_FAIL,
      payload,
    });

    return payload;
  }
};

export const logout = () => async (dispatch) => {
  try {
    await api.post("/auth/logout");
  } catch {
    // Continue local cleanup even if API logout fails.
  }

  saveUserToStorage(null);

  dispatch({ type: actionTypes.USER_LOGOUT });
  dispatch({ type: actionTypes.USER_AUTH_RESET });
  dispatch({ type: actionTypes.USER_LOGIN_RESET });
  dispatch({ type: actionTypes.USER_REGISTER_RESET });
  dispatch({ type: actionTypes.USER_EMAIL_VERIFY_RESET });
  dispatch({ type: actionTypes.USER_EMAIL_RESEND_RESET });
  dispatch({ type: actionTypes.USER_PROFILE_UPDATE_RESET });
  dispatch({ type: actionTypes.USER_EMAIL_CHANGE_REQUEST_RESET });
  dispatch({ type: actionTypes.USER_EMAIL_CHANGE_VERIFY_RESET });
  dispatch({ type: actionTypes.USER_FORGOT_PASSWORD_RESET });
  dispatch({ type: actionTypes.USER_RESET_PASSWORD_RESET });
  dispatch({ type: actionTypes.ADMIN_USER_LIST_RESET });
  dispatch({ type: actionTypes.ADMIN_USER_DETAILS_RESET });
  dispatch({ type: actionTypes.ADMIN_USER_UPDATE_RESET });
  dispatch({ type: actionTypes.ADMIN_USER_DELETE_RESET });

  dispatch({ type: cartActionTypes.CART_RESET_STATE });

  return true;
};

/* -------------------------------------------------------------------------- */
/*                                  Profile                                   */
/* -------------------------------------------------------------------------- */

export const getUserProfile = () => async (dispatch) => {
  try {
    dispatch({ type: actionTypes.USER_AUTH_REQUEST });

    const { data } = await api.get("/auth/me");

    const user = saveUserToStorage(data.user);

    dispatch({
      type: actionTypes.USER_AUTH_SUCCESS,
      payload: user,
    });

    return user;
  } catch (error) {
    if (error?.response?.status === 401) {
      saveUserToStorage(null);
      dispatch({ type: actionTypes.USER_AUTH_RESET });
    } else {
      dispatch({
        type: actionTypes.USER_AUTH_FAIL,
        payload: getErrorMessage(error),
      });
    }

    return null;
  }
};

export const updateUserProfile = (payload) => async (dispatch) => {
  try {
    dispatch({ type: actionTypes.USER_PROFILE_UPDATE_REQUEST });

    const { data } = await api.patch("/auth/me", payload);

    const user = saveUserToStorage(data.user);

    dispatch({
      type: actionTypes.USER_PROFILE_UPDATE_SUCCESS,
      payload: user,
    });

    dispatch({
      type: actionTypes.USER_AUTH_SUCCESS,
      payload: user,
    });

    return {
      ...data,
      user,
    };
  } catch (error) {
    dispatch({
      type: actionTypes.USER_PROFILE_UPDATE_FAIL,
      payload: getErrorMessage(error),
    });

    return null;
  }
};

export const requestUserEmailChange = (email) => async (dispatch) => {
  try {
    dispatch({ type: actionTypes.USER_EMAIL_CHANGE_REQUEST_REQUEST });

    const { data } = await api.post("/auth/me/email-change/request", {
      email,
    });

    dispatch({
      type: actionTypes.USER_EMAIL_CHANGE_REQUEST_SUCCESS,
      payload: data,
    });

    return data;
  } catch (error) {
    dispatch({
      type: actionTypes.USER_EMAIL_CHANGE_REQUEST_FAIL,
      payload: getErrorMessage(error),
    });

    return null;
  }
};

export const verifyUserEmailChange = (code) => async (dispatch) => {
  try {
    dispatch({ type: actionTypes.USER_EMAIL_CHANGE_VERIFY_REQUEST });

    const { data } = await api.post("/auth/me/email-change/verify", {
      code,
    });
    const user = saveUserToStorage(data.user);

    dispatch({
      type: actionTypes.USER_EMAIL_CHANGE_VERIFY_SUCCESS,
      payload: {
        ...data,
        user,
      },
    });

    dispatch({
      type: actionTypes.USER_AUTH_SUCCESS,
      payload: user,
    });

    return {
      ...data,
      user,
    };
  } catch (error) {
    dispatch({
      type: actionTypes.USER_EMAIL_CHANGE_VERIFY_FAIL,
      payload: getErrorMessage(error),
    });

    return null;
  }
};

export const updateDefaultShippingAddress =
  (shippingAddress = {}) =>
  async (dispatch) => {
    try {
      dispatch({
        type: actionTypes.USER_DEFAULT_SHIPPING_ADDRESS_UPDATE_REQUEST,
      });

      const { data } = await api.patch(
        "/auth/me/default-shipping-address",
        shippingAddress,
      );

      const user = saveUserToStorage(data.user);

      dispatch({
        type: actionTypes.USER_DEFAULT_SHIPPING_ADDRESS_UPDATE_SUCCESS,
        payload: user,
      });

      dispatch({
        type: actionTypes.USER_AUTH_SUCCESS,
        payload: user,
      });

      return {
        ...data,
        user,
      };
    } catch (error) {
      const message = getErrorMessage(error);

      dispatch({
        type: actionTypes.USER_DEFAULT_SHIPPING_ADDRESS_UPDATE_FAIL,
        payload: message,
      });

      throw error;
    }
  };

/* -------------------------------------------------------------------------- */
/*                              Password recovery                             */
/* -------------------------------------------------------------------------- */

export const forgotPassword = (email) => async (dispatch) => {
  try {
    dispatch({ type: actionTypes.USER_FORGOT_PASSWORD_REQUEST });

    const { data } = await api.post("/auth/forgot-password", { email });

    dispatch({
      type: actionTypes.USER_FORGOT_PASSWORD_SUCCESS,
      payload: data.message,
    });

    return data;
  } catch (error) {
    dispatch({
      type: actionTypes.USER_FORGOT_PASSWORD_FAIL,
      payload: getErrorMessage(error),
    });

    return null;
  }
};

export const resetPassword = (token, password) => async (dispatch) => {
  try {
    dispatch({ type: actionTypes.USER_RESET_PASSWORD_REQUEST });

    const { data } = await api.post(`/auth/reset-password/${token}`, {
      password,
    });

    const user = saveUserToStorage(data.user);

    dispatch({ type: actionTypes.USER_RESET_PASSWORD_SUCCESS });

    dispatch({
      type: actionTypes.USER_AUTH_SUCCESS,
      payload: user,
    });

    await dispatch(mergeGuestCart());
    await dispatch(getCart());

    return {
      ...data,
      user,
    };
  } catch (error) {
    dispatch({
      type: actionTypes.USER_RESET_PASSWORD_FAIL,
      payload: getErrorMessage(error),
    });

    return null;
  }
};

/* -------------------------------------------------------------------------- */
/*                                Admin users                                 */
/* -------------------------------------------------------------------------- */

export const adminGetUsers =
  ({ page = 1, limit = 20 } = {}) =>
  async (dispatch) => {
    try {
      dispatch({ type: actionTypes.ADMIN_USER_LIST_REQUEST });

      const { data } = await api.get("/auth/users", {
        params: { page, limit },
      });

      const users = Array.isArray(data.users)
        ? data.users.map(normalizeUser).filter(Boolean)
        : [];

      dispatch({
        type: actionTypes.ADMIN_USER_LIST_SUCCESS,
        payload: {
          users,
          total: data.total || 0,
          page: data.page || page,
          pages: data.pages || 1,
        },
      });

      return users;
    } catch (error) {
      dispatch({
        type: actionTypes.ADMIN_USER_LIST_FAIL,
        payload: getErrorMessage(error),
      });

      return null;
    }
  };

export const adminGetUserById = (id) => async (dispatch) => {
  try {
    dispatch({ type: actionTypes.ADMIN_USER_DETAILS_REQUEST });

    const { data } = await api.get(`/auth/users/${id}`);

    const user = normalizeUser(data.user);

    dispatch({
      type: actionTypes.ADMIN_USER_DETAILS_SUCCESS,
      payload: user,
    });

    return user;
  } catch (error) {
    dispatch({
      type: actionTypes.ADMIN_USER_DETAILS_FAIL,
      payload: getErrorMessage(error),
    });

    return null;
  }
};

export const adminCreateUser = (payload) => async (dispatch) => {
  try {
    dispatch({ type: actionTypes.ADMIN_USER_CREATE_REQUEST });

    const requestPayload = {
      ...payload,
      roles: normalizeRoles(payload?.roles),
    };

    const { data } = await api.post("/auth/users", requestPayload);
    const user = normalizeUser(data.user);

    dispatch({
      type: actionTypes.ADMIN_USER_CREATE_SUCCESS,
      payload: user,
    });

    return user;
  } catch (error) {
    dispatch({
      type: actionTypes.ADMIN_USER_CREATE_FAIL,
      payload: getErrorMessage(error),
    });

    return null;
  }
};

export const adminUpdateUser = (id, payload) => async (dispatch, getState) => {
  try {
    dispatch({ type: actionTypes.ADMIN_USER_UPDATE_REQUEST });

    const requestPayload = {
      ...payload,
    };

    if (requestPayload.roles !== undefined) {
      requestPayload.roles = normalizeRoles(requestPayload.roles);
    }

    const { data } = await api.patch(`/auth/users/${id}`, requestPayload);

    const user = normalizeUser(data.user);

    dispatch({
      type: actionTypes.ADMIN_USER_UPDATE_SUCCESS,
      payload: user,
    });

    const currentUser = getState()?.userAuth?.userInfo;
    const currentUserId = currentUser?.id || currentUser?._id;
    const updatedUserId = user?.id || user?._id;

    if (
      currentUserId &&
      updatedUserId &&
      String(currentUserId) === String(updatedUserId)
    ) {
      const savedUser = saveUserToStorage(user);

      dispatch({
        type: actionTypes.USER_AUTH_SUCCESS,
        payload: savedUser,
      });
    }

    return user;
  } catch (error) {
    dispatch({
      type: actionTypes.ADMIN_USER_UPDATE_FAIL,
      payload: getErrorMessage(error),
    });

    return null;
  }
};

export const adminDeleteUser = (id) => async (dispatch) => {
  try {
    dispatch({ type: actionTypes.ADMIN_USER_DELETE_REQUEST });

    const { data } = await api.delete(`/auth/users/${id}`);

    dispatch({
      type: actionTypes.ADMIN_USER_DELETE_SUCCESS,
      payload: data.user?.id || id,
    });

    return true;
  } catch (error) {
    dispatch({
      type: actionTypes.ADMIN_USER_DELETE_FAIL,
      payload: getErrorMessage(error),
    });

    return false;
  }
};
